import { GraphQLClient, gql } from 'graphql-request';

// Get the GraphQL URL from environment variable or fallback
const WP_GRAPHQL_URL = import.meta.env.VITE_WP_GRAPHQL_URL || 'https://palegreen-duck-273209.hostingersite.com/graphql';

// Log the URL in development to help with debugging
if (import.meta.env.DEV) {
  console.log('[WordPress] GraphQL URL:', WP_GRAPHQL_URL);
}

let _client: GraphQLClient | null = null;

export const getWPClient = (): GraphQLClient => {
  if (!_client) {
    _client = new GraphQLClient(WP_GRAPHQL_URL, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
  return _client;
};

// Generic fetch function for archive pages
export async function fetchArchiveItems<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  try {
    const client = getWPClient();
    const data = await client.request<T>(query, variables);
    return data;
  } catch (error) {
    console.error('GraphQL fetch error:', error);
    console.error('GraphQL URL being used:', WP_GRAPHQL_URL);
    // Return object with empty nodes instead of empty object
    // This ensures the calling code can safely access .nodes
    throw error;
  }
}

// Safe fetch that returns empty nodes on error
export async function fetchArchiveItemsSafe<T extends Record<string, { nodes: unknown[] }>>(
  query: string,
  key: string,
  variables?: Record<string, unknown>
): Promise<T[keyof T]['nodes']> {
  try {
    const client = getWPClient();
    const data = await client.request<T>(query, variables);
    const result = data[key];
    if (!result || !Array.isArray(result.nodes)) {
      console.warn(`No nodes found for key: ${key}`, data);
      return [] as T[keyof T]['nodes'];
    }
    return result.nodes;
  } catch (error) {
    console.error(`GraphQL fetch error for ${key}:`, error);
    return [] as T[keyof T]['nodes'];
  }
}

// GraphQL Queries for each content type
export const POSTS_QUERY = gql`
  query GetPosts($first: Int = 12) {
    posts(first: $first) {
      nodes {
        id
        title
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
      }
    }
  }
`;

export const KNOWLEDGE_QUERY = gql`
  query GetKnowledge($first: Int = 12) {
    knowledgeItems(first: $first) {
      nodes {
        id
        title
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
      }
    }
  }
`;

export const GUIDES_QUERY = gql`
  query GetGuides($first: Int = 12) {
    guides(first: $first) {
      nodes {
        id
        title
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
      }
    }
  }
`;

export const TOOLS_QUERY = gql`
  query GetTools($first: Int = 12) {
    tools(first: $first) {
      nodes {
        id
        title
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
      }
    }
  }
`;

export const LEGISLATION_QUERY = gql`
  query GetLegislation($first: Int = 12) {
    legislationItems(first: $first) {
      nodes {
        id
        title
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
      }
    }
  }
`;

export const ORGANIZATIONS_QUERY = gql`
  query GetOrganizations($first: Int = 12) {
    organizations(first: $first) {
      nodes {
        id
        title
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
      }
    }
  }
`;

export const RESOURCES_QUERY = gql`
  query GetResources($first: Int = 12) {
    resources(first: $first) {
      nodes {
        id
        title
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
      }
    }
  }
`;

export const FAQS_QUERY = gql`
  query GetFaqs($first: Int = 12) {
    faqs(first: $first) {
      nodes {
        id
        title
        content
        slug
        date
      }
    }
  }
`;

// Types
export interface ArchiveItem {
  id: string;
  title: string;
  excerpt?: string;
  content?: string;
  slug: string;
  date: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText: string;
    };
  };
}

// Test function to verify WordPress connection
export async function testWordPressConnection() {
  console.log('Testing WordPress connection...');
  console.log('GraphQL URL:', WP_GRAPHQL_URL);
  
  try {
    const client = getWPClient();
    
    // Simple test query
    const testQuery = gql`
      query TestConnection {
        posts(first: 1) {
          nodes {
            id
            title
            slug
          }
        }
      }
    `;
    
    const result = await client.request(testQuery);
    console.log('✅ WordPress connection successful!');
    console.log('Test result:', JSON.stringify(result, null, 2));
    
    // Test fetching posts
    console.log('\nTesting posts fetch...');
    const posts = await fetchArchiveItemsSafe<{ posts: { nodes: ArchiveItem[] } }>(
      POSTS_QUERY,
      'posts',
      { first: 3 }
    );
    console.log(`✅ Fetched ${posts.length} posts`);
    if (posts.length > 0) {
      console.log('First post:', posts[0]);
    }
    
    return {
      success: true,
      postsCount: posts.length,
      data: result
    };
  } catch (error) {
    console.error('❌ WordPress connection failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}
